import { forwardRef, useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeSlash, MagnifyingGlass } from '@phosphor-icons/react';

export const NexusInput = forwardRef(({
  label,
  type = 'text',
  error,
  helperText,
  maxLength,
  showCount = false,
  className = '',
  inputClassName = '',
  ...props
}, ref) => {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [charCount, setCharCount] = useState(props.value?.length || 0);
  
  const inputType = type === 'password' && showPassword ? 'text' : type;
  
  const handleChange = (e) => {
    if (showCount || maxLength) {
      setCharCount(e.target.value.length);
    }
    props.onChange?.(e);
  };
  
  return (
    <div className={`relative ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          className={`
            block text-sm font-medium mb-2 transition-colors duration-200
            ${focused ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'}
            ${error ? 'text-nexus-error' : ''}
          `}
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {type === 'search' && (
          <MagnifyingGlass 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-nexus-text-tertiary"
            weight="duotone"
          />
        )}
        
        <input
          ref={ref}
          id={id}
          type={inputType}
          style={{ backgroundColor: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)' }}
          className={`
            w-full border
            px-4 py-2.5 rounded-sm
            placeholder:text-nexus-text-tertiary
            transition-all duration-200
            focus:outline-none
            ${type === 'search' ? 'pl-10' : ''}
            ${type === 'password' ? 'pr-10' : ''}
            ${focused && !error ? 'border-nexus-accent-mint ring-1 ring-nexus-accent-mint shadow-glow-mint' : 'border-nexus-border-subtle'}
            ${error ? 'border-nexus-error ring-1 ring-nexus-error animate-shake' : ''}
            ${inputClassName}
          `}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={handleChange}
          maxLength={maxLength}
          {...props}
        />
        
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-nexus-text-tertiary hover:text-nexus-text-primary transition-colors"
          >
            {showPassword ? (
              <EyeSlash className="w-5 h-5" weight="duotone" />
            ) : (
              <Eye className="w-5 h-5" weight="duotone" />
            )}
          </button>
        )}
      </div>
      
      <div className="flex justify-between mt-1.5">
        <AnimatePresence>
          {(error || helperText) && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className={`text-xs ${error ? 'text-nexus-error' : 'text-nexus-text-tertiary'}`}
            >
              {error || helperText}
            </motion.p>
          )}
        </AnimatePresence>
        
        {showCount && (
          <span className={`text-xs ${charCount === maxLength ? 'text-nexus-error' : 'text-nexus-text-tertiary'}`}>
            {charCount}{maxLength ? `/${maxLength}` : ''}
          </span>
        )}
      </div>
    </div>
  );
});

NexusInput.displayName = 'NexusInput';

export const NexusTextarea = forwardRef(({
  label,
  error,
  helperText,
  maxLength,
  showCount = false,
  rows = 4,
  className = '',
  ...props
}, ref) => {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [charCount, setCharCount] = useState(props.value?.length || 0);
  
  const handleChange = (e) => {
    if (showCount || maxLength) {
      setCharCount(e.target.value.length);
    }
    props.onChange?.(e);
  };
  
  return (
    <div className={`relative ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          className={`
            block text-sm font-medium mb-2 transition-colors duration-200
            ${focused ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'}
            ${error ? 'text-nexus-error' : ''}
          `}
        >
          {label}
        </label>
      )}
      
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        style={{ backgroundColor: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)' }}
        className={`
          w-full border
          px-4 py-2.5 rounded-sm resize-none
          placeholder:text-nexus-text-tertiary
          transition-all duration-200
          focus:outline-none
          ${focused && !error ? 'border-nexus-accent-mint ring-1 ring-nexus-accent-mint shadow-glow-mint' : 'border-nexus-border-subtle'}
          ${error ? 'border-nexus-error ring-1 ring-nexus-error' : ''}
        `}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={handleChange}
        maxLength={maxLength}
        {...props}
      />
      
      <div className="flex justify-between mt-1.5">
        <AnimatePresence>
          {(error || helperText) && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className={`text-xs ${error ? 'text-nexus-error' : 'text-nexus-text-tertiary'}`}
            >
              {error || helperText}
            </motion.p>
          )}
        </AnimatePresence>
        
        {showCount && (
          <span className={`text-xs ${charCount === maxLength ? 'text-nexus-error' : 'text-nexus-text-tertiary'}`}>
            {charCount}{maxLength ? `/${maxLength}` : ''}
          </span>
        )}
      </div>
    </div>
  );
});

NexusTextarea.displayName = 'NexusTextarea';
